import { useClubImportNudgeTracking } from "@/components/features/ClubImport/useClubImportNudgeTracking";
import { Solving } from "@/components/organisms/SolutionView/SolveStatus";
import { Dialog } from "@/design-system/Dialog/Dialog";
import { trackClubImportCta } from "@/events/clubImportMixpanel";
import { getSbcsSelector } from "@/redux/sbcs/sbcsSlice";
import { defaultSquadBuilderSelector } from "@/redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { useTranslation } from "@repo/shared/i18n";
import {
  type ClubImportNudgeData,
  type ClubImportNudgeId,
  acceptClubImportNudge,
  clubImportNudgeOrigin,
  dismissClubImportNudge,
} from "@repo/shared/redux";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import type { APIStatus } from "../../../api/APIStatus.type";

/** Copy lives under clubImport:nudge.<key>; ids are kebab, keys camel. */
const COPY_KEY = {
  "sbc-solver": "sbcSolver",
  "squad-builder-build": "squadBuilderBuild",
  "squad-builder-insert": "squadBuilderInsert",
  "evo-builder-overview": "evoBuilderOverview",
  "evo-builder-player": "evoBuilderPlayer",
  "evo-players": "evoPlayers",
  "players-overview": "playersOverview",
} as const satisfies Record<ClubImportNudgeId, string>;

/**
 * The request a nudge rides on, when it rides on one: the SBC solve and the
 * squad build both raise their nudge as they start. Null for the rest.
 */
const useNudgeProgress = (nudgeId: ClubImportNudgeId): APIStatus | null => {
  const { solutionStatus } = useSelector(getSbcsSelector);
  const { statusBuildSquad } = useSelector(defaultSquadBuilderSelector);
  if (nudgeId === "sbc-solver") return solutionStatus;
  if (nudgeId === "squad-builder-build") return statusBuildSquad;
  return null;
};

/**
 * The in-context pitch for a first club import (see clubImportNudge.ts).
 *
 * Raised while the user is doing the thing the import would improve, so it
 * dims rather than blurs: the SBC solver's nudge stays up while the solution
 * lands behind it, and the title follows the request.
 */
export const ClubImportNudgeDialog = ({
  data,
  onClose,
}: {
  data?: ClubImportNudgeData;
  onClose: () => void;
}) => {
  const { t } = useTranslation("clubImport");
  const nudgeId = data?.nudgeId ?? "sbc-solver";
  const origin = data?.origin ?? clubImportNudgeOrigin(nudgeId);
  const { trackExit } = useClubImportNudgeTracking(nudgeId, origin);
  // Every exit path ends in onClose, so the cool-off is written once, here.
  const decided = useRef(false);
  const copy = COPY_KEY[nudgeId];
  const progress = useNudgeProgress(nudgeId);

  // A failed request (anonymous limit, solver error) raises its own modal or
  // panel; the nudge steps aside and, not being the user's answer, writes no
  // cool-off.
  useEffect(() => {
    if (progress !== "rejected") return;
    decided.current = true;
    onClose();
  }, [progress, onClose]);

  const dismiss = () => {
    if (decided.current) return;
    decided.current = true;
    trackExit("negative");
    dismissClubImportNudge();
  };

  return (
    <Dialog
      isOpen
      backdrop="dimmed"
      title={<NudgeTitle nudgeId={nudgeId} progress={progress} />}
      negLabel={t(`nudge.${copy}.dismiss`)}
      negVariant="subtle"
      posLabel={t(`nudge.${copy}.cta`)}
      // A new tab, so the result behind the dialog is still there afterwards.
      posHref="/club-import-tutorial"
      posHrefNewTab
      onClose={() => {
        dismiss();
        onClose();
      }}
      onNegative={dismiss}
      onPositive={() => {
        decided.current = true;
        trackExit("positive");
        acceptClubImportNudge({ nudgeId, origin });
        trackClubImportCta(origin);
      }}
    >
      <p>
        {/* While the request runs the pitch reads as a tip, not an ask. */}
        {progress === "pending" &&
        (nudgeId === "sbc-solver" || nudgeId === "squad-builder-build")
          ? t(`nudge.${COPY_KEY[nudgeId]}.tip`)
          : t(`nudge.${copy}.body`)}
      </p>
    </Dialog>
  );
};

/**
 * Stands in for the request's own status panel while the nudge is up: the
 * wait, then "ready". Nudges without a request get the generic title.
 */
const NudgeTitle = ({
  nudgeId,
  progress,
}: {
  nudgeId: ClubImportNudgeId;
  progress: APIStatus | null;
}) => {
  const { t } = useTranslation(["clubImport", "squadBuilder"]);

  if (nudgeId === "sbc-solver") {
    if (progress === "pending")
      return <Solving textClassName="text-[19px] leading-[1.25]" />;
    return <>{t("nudge.solutionReady")}</>;
  }
  if (nudgeId === "squad-builder-build") {
    if (progress === "pending")
      return (
        <Solving
          label={t("squadBuilder:build.buildingMetaSquads")}
          textClassName="text-[19px] leading-[1.25]"
        />
      );
    return <>{t("nudge.squadsReady")}</>;
  }
  return <>{t("nudge.title")}</>;
};
